import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Hash, Loader2, Sparkles } from 'lucide-react';
import PrayerCard from '../components/PrayerCard';
import { listPrayers } from '../lib/api';

/**
 * Results page for a single hashtag. Reuses the standard PrayerCard grid so
 * the layout matches the Home page. Backend does the normalization; we just
 * forward the raw param.
 */
const HashtagResults = () => {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Display value: keep the URL tag lowercase for consistency with backend
  // normalization but strip any stray '#' the user may have URL-encoded.
  const displayTag = (tag || '').replace(/^#+/, '');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listPrayers({ hashtag: displayTag, limit: 60, sort: 'latest' })
      .then((data) => { if (!cancelled) setPrayers(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setError('Could not load prayers for this hashtag.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [displayTag]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="hashtag-results-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        data-testid="hashtag-back-btn"
        className="inline-flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white mb-4"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-full bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-600 dark:text-amber-300 flex-shrink-0">
          <Hash size={20} />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="hashtag-title">
            Prayers tagged <span className="text-amber-600 dark:text-amber-400">#{displayTag}</span>
          </h1>
          {!loading && !error && (
            <div className="text-sm text-zinc-500" data-testid="hashtag-count">
              {prayers.length} {prayers.length === 1 ? 'prayer' : 'prayers'}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2" data-testid="hashtag-loading">
          <Loader2 className="animate-spin" size={16} /> Loading prayers…
        </div>
      ) : error ? (
        <div className="py-10 text-center text-red-600 dark:text-red-400" data-testid="hashtag-error">{error}</div>
      ) : prayers.length === 0 ? (
        <div className="py-16 text-center max-w-md mx-auto" data-testid="hashtag-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 mb-3">
            <Sparkles size={22} />
          </div>
          <h2 className="text-base font-medium mb-1">No prayers yet</h2>
          <p className="text-sm text-zinc-500 mb-4">
            No one has shared a prayer with <span className="font-medium text-amber-600 dark:text-amber-400">#{displayTag}</span> yet.
            Be the first.
          </p>
          <Link
            to="/add-prayer"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold"
          >
            Share a prayer
          </Link>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-x-4 gap-y-8 pt-4 pb-10"
          data-testid="hashtag-prayer-grid"
        >
          {prayers.map((p) => <PrayerCard key={p.id} prayer={p} />)}
        </div>
      )}
    </div>
  );
};

export default HashtagResults;
